import { Loading } from "../../app/Status";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import {
  COMPANY_KIND_LABELS,
  CONTACT_LINK_ROLE_LABELS,
  CONTACT_LINK_ROLES,
  TRADES,
  todayIso,
  type BuildingContactsDto,
  type BuildingDto,
  type CompanyDto,
  type ContactLinkDto,
  type ContactLinkRole,
  type DirectoryPersonDto,
  type Trade,
} from "@klantpunt/shared";
import { Combobox, type ComboItem } from "../../app/Combobox";
import { Modal } from "../../app/Modal";
import { hasModule, useMe } from "../../lib/auth";
import { IconPencil } from "../../app/icons";
import { buildingPath } from "../../app/navItems";
import { api } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { errMsg, PersonDialog, useDirectoryRefresh } from "./DirectoryDialogs";
import { QuickCompanyDialog } from "./QuickAdd";
import { companyPath, fmtDate, Phone } from "./parts";
import { Meta } from "../../app/Meta";

const contactsKey = (buildingId: string) => ["building-contacts", buildingId] as const;

/** Plek binnen het gebouw: "Hele gebouw", of verdieping en ruimte. */
const placeLabel = (l: ContactLinkDto) =>
  l.room ? `${l.floor?.name ?? ""} · ${l.room.name}`.replace(/^ · /, "") : (l.floor?.name ?? "Hele gebouw");

/** Periode alleen tonen als die iets zegt: start in de toekomst of een einddatum. */
function periodLabel(l: ContactLinkDto, today: string) {
  if (l.validFrom > today) return `vanaf ${fmtDate(l.validFrom)}`;
  if (l.validTo && l.validTo < today) return `${fmtDate(l.validFrom)} – ${fmtDate(l.validTo)}`;
  if (l.validTo) return `tot en met ${fmtDate(l.validTo)}`;
  return null;
}

const partyName = (l: ContactLinkDto) => l.company?.name ?? l.person?.name ?? "";
const partyHref = (l: ContactLinkDto) => (l.company ? companyPath(l.company.id) : `/adresboek?persoon=${l.person!.id}`);

// ---------- tab Contacten van een gebouw ----------

const GROUPS: { title: string; roles: ContactLinkRole[]; empty: string }[] = [
  { title: "Huurders", roles: ["tenant"], empty: "Geen huurders vastgelegd." },
  { title: "Contactpersonen", roles: ["contact"], empty: "Geen contactpersonen vastgelegd." },
  {
    title: "Sleutelhouders en noodcontacten",
    roles: ["key_holder", "emergency"],
    empty: "Geen sleutelhouders of noodcontacten.",
  },
  { title: "Vaste leveranciers", roles: ["supplier"], empty: "Geen vaste leveranciers vastgelegd." },
];

/**
 * Tab Contacten (docs/ADRESBOEK.md, stap 2): de klant met zijn contactpersonen (alleen lezen) en wie er bij
 * het gebouw hoort: huurders, contactpersonen, sleutelhouders en noodcontacten, vaste leveranciers.
 */
export function BuildingContacts({
  building,
  canEdit,
  canManageClients,
  adding,
  onAddDone,
}: {
  building: BuildingDto;
  canEdit: boolean;
  canManageClients: boolean;
  /** Dialoog "Koppelen" open (knop staat in de paginakop). */
  adding: boolean;
  onAddDone: () => void;
}) {
  const { data, isPending, error } = useQuery({
    queryKey: contactsKey(building.id),
    queryFn: () => api<BuildingContactsDto>(`/buildings/${building.id}/contacts`),
  });
  const [showPast, setShowPast] = useState(false);
  const [editing, setEditing] = useState<ContactLinkDto | null>(null);
  const today = todayIso();

  if (isPending) return <Loading page />;
  if (error || !data) return <div className="alert">De contacten konden niet worden geladen.</div>;
  const past = data.links.filter((l) => !l.current && l.validFrom <= today);
  const shown = data.links.filter((l) => showPast || l.current || l.validFrom > today);

  return (
    <div className="dir-contacts">
      <section className="panel" aria-labelledby="bc-client">
        <h2 id="bc-client" className="dir-group-title">
          Klant
        </h2>
        <p>
          <strong>{data.client.isUnassigned ? "Geen klant" : data.client.name}</strong>
        </p>
        {data.client.contacts.length > 0 && (
          <ul className="dir-items">
            {data.client.contacts.map((c) => (
              <li key={c.id}>
                <span className="grow">
                  {c.name}
                  {c.isPrimary && <span className="chip dir-kind"> Hoofdcontact</span>}
                  {c.jobTitle && <div className="muted small">{c.jobTitle}</div>}
                </span>
                <Phone n={c.phone} label={c.name} />
              </li>
            ))}
          </ul>
        )}
        <p className="hint">
          Contactpersonen van de klant beheer je bij de klant
          {canManageClients && (
            <>
              {" "}
              (<Link to="/gebruikers?tab=klanten">Organisatie → Klanten</Link>)
            </>
          )}
          .
        </p>
      </section>

      {GROUPS.map((g) => {
        const items = shown.filter((l) => g.roles.includes(l.role));
        const id = `bc-${g.roles[0]}`;
        return (
          <section key={g.title} className="panel" aria-labelledby={id}>
            <h2 id={id} className="dir-group-title">
              {g.title}
            </h2>
            {items.length ? (
              <ul className="dir-items">
                {items.map((l) => (
                  <LinkRow key={l.id} l={l} today={today} onEdit={canEdit ? () => setEditing(l) : undefined} />
                ))}
              </ul>
            ) : (
              <p className="muted small">{g.empty}</p>
            )}
          </section>
        );
      })}

      {past.length > 0 && (
        <button type="button" className="btn small ghost" onClick={() => setShowPast(!showPast)}>
          {showPast ? "Eerdere verbergen" : `Eerdere tonen (${past.length})`}
        </button>
      )}

      {(adding || editing) && (
        <LinkDialog
          building={building}
          link={editing ?? undefined}
          onClose={() => {
            setEditing(null);
            onAddDone();
          }}
        />
      )}
    </div>
  );
}

function LinkRow({ l, today, onEdit }: { l: ContactLinkDto; today: string; onEdit?: () => void }) {
  const period = periodLabel(l, today);
  const ended = !l.current && l.validFrom <= today;
  const sub = [
    placeLabel(l),
    l.role === "emergency" || l.role === "key_holder" ? CONTACT_LINK_ROLE_LABELS[l.role] : null,
    l.trade,
    l.person?.companyName,
    l.person?.jobTitle,
    period,
  ].filter(Boolean);
  // Bij een leverancier telt het storingsnummer; bij een persoon het mobiele nummer.
  const phone = l.company ? (l.company.emergencyPhone ?? l.company.phone) : (l.person?.mobile ?? l.person?.phone);
  return (
    <li className={ended ? "dir-ended" : undefined}>
      <span className="grow">
        <Link to={partyHref(l)}>{partyName(l)}</Link>
        {l.isPrimary && <span className="chip dir-kind"> Hoofdcontact</span>}
        {ended && <span className="chip dir-kind"> Afgelopen</span>}
        <Meta className="muted small" items={sub} />
        {l.person?.availability && <div className="muted small">{l.person.availability}</div>}
        {l.notes && <div className="small dir-notes">{l.notes}</div>}
      </span>
      {phone && (
        <span className="dir-phone">
          {l.company?.emergencyPhone && <span className="label">Storing</span>}
          <Phone n={phone} label={partyName(l)} />
        </span>
      )}
      {onEdit && (
        <button
          type="button"
          className="btn ghost small ico"
          aria-label={`Koppeling ${partyName(l)} bewerken`}
          onClick={onEdit}
        >
          <IconPencil aria-hidden="true" />
        </button>
      )}
    </li>
  );
}

// ---------- koppelen ----------

/** Bedrijf of persoon aan het gebouw koppelen, of een koppeling bewerken / beëindigen / verwijderen. */
export function LinkDialog({
  building,
  link,
  onClose,
}: {
  building: BuildingDto;
  link?: ContactLinkDto;
  onClose: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const refresh = useDirectoryRefresh();
  const [party, setParty] = useState<"company" | "person">(link?.person ? "person" : "company");
  const [partyId, setPartyId] = useState(link?.company?.id ?? link?.person?.id ?? "");
  const { data: me } = useMe();
  const canAdd = hasModule(me, "directory", "edit");
  const [adding, setAdding] = useState<string | null>(null);
  const [role, setRole] = useState<ContactLinkRole>(link?.role ?? "tenant");
  const [trade, setTrade] = useState<Trade | "">(link?.trade ?? "");
  const [floorId, setFloorId] = useState(link?.floor?.id ?? "");
  const [roomId, setRoomId] = useState(link?.room?.id ?? "");
  const [validFrom, setValidFrom] = useState(link?.validFrom ?? todayIso());
  const [validTo, setValidTo] = useState(link?.validTo ?? "");
  const [isPrimary, setIsPrimary] = useState(link?.isPrimary ?? false);
  const [notes, setNotes] = useState(link?.notes ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const companies = useQuery({
    queryKey: ["companies", "0"],
    queryFn: () => api<CompanyDto[]>("/companies"),
    enabled: !link,
  }).data;
  const people = useQuery({
    queryKey: ["people", "0"],
    queryFn: () => api<DirectoryPersonDto[]>("/people"),
    enabled: !link,
  }).data;
  const floors = [...building.floors].sort((a, b) => a.sort - b.sort);
  const rooms = building.rooms.filter((r) => r.floorId === floorId);

  async function run(fn: () => Promise<unknown>, ok: string) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await Promise.all([qc.invalidateQueries({ queryKey: contactsKey(building.id) }), refresh()]);
      toast(ok);
      onClose();
    } catch (err) {
      setError(errMsg(err));
      setBusy(false);
    }
  }
  const fields = {
    role,
    trade: role === "supplier" && trade ? trade : null,
    floorId: floorId || null,
    roomId: roomId || null,
    validFrom,
    validTo: validTo || null,
    isPrimary,
    notes,
  };
  const save = (e?: FormEvent) => {
    e?.preventDefault();
    void run(
      () =>
        link
          ? api(`/contact-links/${link.id}`, { method: "PATCH", body: fields })
          : api(`/buildings/${building.id}/contacts`, {
              method: "POST",
              body: {
                id: crypto.randomUUID(),
                ...fields,
                companyId: party === "company" ? partyId : null,
                personId: party === "person" ? partyId : null,
              },
            }),
      "Koppeling opgeslagen",
    );
  };

  const options: ComboItem[] =
    party === "company"
      ? (companies ?? []).map((c) => ({
          value: c.id,
          label: c.name,
          meta: [c.kinds.map((k) => COMPANY_KIND_LABELS[k]).join(", "), c.trades.join(", ")]
            .filter(Boolean)
            .join(" \u00b7 "),
        }))
      : (people ?? []).map((p) => ({ value: p.id, label: p.name, meta: p.company?.name ?? p.jobTitle ?? undefined }));

  return (
    <Modal
      title={link ? `Koppeling ${partyName(link)}` : "Koppelen aan dit gebouw"}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          {link && (
            <button
              type="button"
              className="btn ghost danger"
              disabled={busy}
              onClick={() =>
                void run(() => api(`/contact-links/${link.id}`, { method: "DELETE" }), "Koppeling verwijderd")
              }
            >
              Verwijderen
            </button>
          )}
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={() => save()}
            disabled={busy || (!link && !partyId) || !validFrom}
          >
            Opslaan
          </button>
        </>
      }
    >
      <form onSubmit={save}>
        {error && (
          <div className="alert" role="alert">
            {error}
          </div>
        )}
        {!link && (
          <>
            <fieldset className="field dir-checks">
              <legend className="label">Wie</legend>
              {(
                [
                  ["company", "Bedrijf"],
                  ["person", "Persoon"],
                ] as const
              ).map(([v, label]) => (
                <label key={v} className="dir-check">
                  <input
                    type="radio"
                    name="party"
                    checked={party === v}
                    onChange={() => {
                      setParty(v);
                      setPartyId("");
                    }}
                  />
                  {label}
                </label>
              ))}
            </fieldset>
            <Combobox
              id="contact-party"
              label={party === "company" ? "Bedrijf *" : "Persoon *"}
              items={options}
              value={partyId || null}
              onChange={(v) => setPartyId(v ?? "")}
              placeholder={party === "company" ? "Zoek een bedrijf" : "Zoek een persoon"}
              {...(canAdd
                ? {
                    onAdd: (q: string) => setAdding(q),
                    addLabel: (q: string) =>
                      `${party === "company" ? "Nieuw bedrijf" : "Nieuwe persoon"}${q ? ` \u201c${q}\u201d` : ""}`,
                  }
                : {})}
            />
          </>
        )}
        <div className="grid2">
          <label className="field">
            <span className="label">Rol</span>
            <select value={role} onChange={(e) => setRole(e.target.value as ContactLinkRole)}>
              {CONTACT_LINK_ROLES.map((r) => (
                <option key={r} value={r}>
                  {CONTACT_LINK_ROLE_LABELS[r]}
                </option>
              ))}
            </select>
          </label>
          {role === "supplier" && (
            <label className="field">
              <span className="label">Waarvoor</span>
              <select value={trade} onChange={(e) => setTrade(e.target.value as Trade | "")}>
                <option value="">Algemeen</option>
                {TRADES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        <div className="grid2">
          <label className="field">
            <span className="label">Verdieping</span>
            <select
              value={floorId}
              onChange={(e) => {
                setFloorId(e.target.value);
                setRoomId("");
              }}
            >
              <option value="">Hele gebouw</option>
              {floors.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="label">Ruimte</span>
            <select value={roomId} onChange={(e) => setRoomId(e.target.value)} disabled={!floorId}>
              <option value="">{floorId ? "Hele verdieping" : "Kies eerst een verdieping"}</option>
              {rooms.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="grid2">
          <label className="field">
            <span className="label">Vanaf *</span>
            <input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
          </label>
          <label className="field">
            <span className="label">Tot en met</span>
            <input type="date" value={validTo} min={validFrom} onChange={(e) => setValidTo(e.target.value)} />
          </label>
        </div>
        <p className="hint">Vertrekt een huurder? Vul de einddatum in; de koppeling blijft zichtbaar onder Eerdere.</p>
        <label className="dir-check">
          <input type="checkbox" checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} />
          Hoofdcontact voor deze plek en rol
        </label>
        <label className="field">
          <span className="label">Notitie</span>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="bijv. sleutel in kluis 3" />
        </label>
        <button type="submit" hidden />
      </form>
      {adding !== null && party === "company" && (
        <QuickCompanyDialog
          initialName={adding}
          kind={role === "tenant" ? "tenant" : role === "supplier" ? "supplier" : "other"}
          chooseKind
          onClose={() => setAdding(null)}
          onSaved={(id) => {
            setAdding(null);
            setPartyId(id);
          }}
        />
      )}
      {adding !== null && party === "person" && (
        <PersonDialog
          initialName={adding}
          onClose={() => setAdding(null)}
          onSaved={(id) => {
            setAdding(null);
            setPartyId(id);
          }}
        />
      )}
    </Modal>
  );
}

// ---------- in het adresboek: bij welke gebouwen ----------

/** Paneel van een bedrijf of persoon: de gebouwen en ruimtes waaraan het gekoppeld is. */
export function LinksSection({ links }: { links: ContactLinkDto[] | undefined }) {
  const [showPast, setShowPast] = useState(false);
  if (!links) return null;
  const today = todayIso();
  const past = links.filter((l) => !l.current && l.validFrom <= today);
  const shown = links.filter((l) => showPast || l.current || l.validFrom > today);
  return (
    <section className="dir-sec">
      <h3>Gebouwen en ruimtes</h3>
      {shown.length ? (
        <ul className="dir-items">
          {shown.map((l) => {
            const period = periodLabel(l, today);
            return (
              <li key={l.id} className={!l.current && l.validFrom <= today ? "dir-ended" : undefined}>
                <span className="grow">
                  <Link to={`${buildingPath(l.building.id, "gebouw")}?tab=contacten`}>
                    <span className="code">{l.building.code}</span> {l.building.name}
                  </Link>
                  <div className="muted small">
                    {[
                      CONTACT_LINK_ROLE_LABELS[l.role] + (l.isPrimary ? " (hoofdcontact)" : ""),
                      l.trade,
                      placeLabel(l),
                      period,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="muted small">
          {past.length ? "Geen lopende koppelingen." : "Nog niet gekoppeld."} Koppelen doe je bij het gebouw, tab
          Contacten.
        </p>
      )}
      {past.length > 0 && (
        <button type="button" className="linkbtn small" onClick={() => setShowPast(!showPast)}>
          {showPast ? "Eerdere verbergen" : `Eerdere tonen (${past.length})`}
        </button>
      )}
    </section>
  );
}
